import React from 'react';
import { assertNever } from '@varos/util-typescript';
import { IntegrationControllerBaseConfig } from '../../../base';
import { CallUpsertController } from './callUpsertInterface';
import { CallUpsertControllerProps, CallUpsertViewProps } from './callUpsertProps';
import { FiPhone, FiMic, FiMicOff } from 'react-icons/fi';
import { FiPhoneOff } from 'react-icons/fi';
import { MdCallEnd, MdStopCircle } from 'react-icons/md';
import { BsMic, BsMicMute } from 'react-icons/bs';

export function createCallUpsertController(
    config: IntegrationControllerBaseConfig
): CallUpsertController {
    const {
        kernel: {
            provider: { createActionController },
        },
    } = config;

    const endController = createActionController({
        id: 'end',
    });

    const muteController = createActionController({
        id: 'mute',
    });

    const interruptController = createActionController({
        id: 'interrupt',
    });

    function getStatus(props: CallUpsertControllerProps): CallUpsertViewProps['status'] {
        if (props.call.item.status === 'connecting') {
            return {
                label: 'Connecting',
                kind: 'connecting',
                colorScheme: 'blue',
                description: 'Connecting to call',
                Icon: FiPhone,
            };
        }
        if (props.call.item.status === 'connected') {
            return {
                label: 'Connected',
                kind: 'connected',
                colorScheme: 'green',
                description: 'Connected to the call',
                Icon: FiPhone,
            };
        }
        if (props.call.item.status === 'ended') {
            return {
                label: 'Ended',
                kind: 'ended',
                colorScheme: 'red',
                description: 'Call ended',
                Icon: FiPhoneOff,
            };
        }
        if (props.call.item.status === 'error') {
            return {
                label: 'Error',
                kind: 'error',
                colorScheme: 'red',
                description: 'Call connection failed',
                Icon: FiPhoneOff,
            };
        }
        assertNever(props.call.item.status);
    }
    return {
        useProps(context, item, props): CallUpsertViewProps {
            const [callStartTime, setCallStartTime] = React.useState<Date | null>(null);
            const [duration, setDuration] = React.useState<string>('00:00');

            // Start timer when call becomes connected
            React.useEffect(() => {
                if (props.call.item.status === 'connected' && !callStartTime) {
                    setCallStartTime(new Date());
                }
            }, [props.call.item.status, callStartTime]);

            // Update duration every second when call is active
            React.useEffect(() => {
                let interval: NodeJS.Timeout | null = null;

                if (props.call.item.status === 'connected' && callStartTime) {
                    interval = setInterval(() => {
                        const now = new Date();
                        const elapsed = Math.floor(
                            (now.getTime() - callStartTime.getTime()) / 1000
                        );
                        const minutes = Math.floor(elapsed / 60);
                        const seconds = elapsed % 60;
                        setDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
                    }, 1000);
                }

                return () => {
                    if (interval) {
                        clearInterval(interval);
                    }
                };
            }, [props.call.item.status, callStartTime]);

            // Reset timer when call ends
            React.useEffect(() => {
                if (
                    props.call.item.status === 'ended' ||
                    props.call.item.status === 'error'
                ) {
                    setCallStartTime(null);
                    setDuration('00:00');
                }
            }, [props.call.item.status]);

            // Create definition object from integration definition item
            const definition = item.definition?.display_name
                ? { display_name: item.definition.display_name }
                : item.integration?.definition_id
                  ? { display_name: item.integration.definition_id }
                  : undefined;

            // Create participants array
            const assistantName = definition?.display_name || 'Assistant';
            const participants = [
                {
                    kind: 'assistant' as const,
                    label: assistantName,
                    speaking: props.call.systemSpeaking,
                    avatar: assistantName.substring(0, 2).toUpperCase(),
                },
                {
                    kind: 'user' as const,
                    label: 'You',
                    speaking: props.call.userSpeaking,
                    avatar: 'YO',
                },
            ];

            const end = endController.useProps({
                kind: 'custom',
                name: 'End call',
                colorScheme: 'red',
                Icon: MdCallEnd,
                onPerform() {
                    return props.call.end();
                },
            });

            const mute = muteController.useProps({
                kind: 'custom',
                name: props.call.muted ? 'Unmute' : 'Mute',
                Icon: props.call.muted ? BsMic : BsMicMute,
                onPerform() {
                    // Update the UI state
                    props.mute.onChange(!props.mute.value);
                },
            });

            const interrupt = interruptController.useProps({
                kind: 'custom',
                name: 'Interrupt',
                Icon: MdStopCircle,
                onPerform() {
                    // Call the interrupt callback
                    props.onInterrupt();
                },
            });

            return {
                status: getStatus(props),
                participants,
                definition,
                title: 'Audio call',
                callDuration: duration,
                end: {
                    button: {
                        ...end.button,
                    },
                },
                action: {
                    items: [mute, interrupt, end],
                },
            };
        },
    };
}
